import {
  GiftDeliveryForAdmin,
  GiftDeliveryForRebatePending,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftRebateSettlementLine } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import { SettlementPendingEntry } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';

import { getItemName } from './delivery.utils';

// ─────────────────────────────────────────────
// 상조 리베이트 정산 테이블 — 행 계산 / 합계 유틸
//
// 정산 예정·배송완료 테이블이 동일한 컬럼/계산식을 공유한다.
// 가격 데이터는 모두 주문 스냅샷(giftOrder.orderDetails[].giftItemEmbed)에서 가져온다.
// ─────────────────────────────────────────────

/** 리베이트 기본 수수료율 (%) — 상조별 giftAccountConfig.feeRate 미설정 시 폴백 */
export const DEFAULT_REBATE_FEE_RATE = 5.3;

/**
 * 표시용 절사: 소수점 둘째 자리에서 버림해 1자리까지 남긴다.
 * (수수료처럼 소수가 나오는 값에서 원화에 없는 긴 소수를 없애되,
 *  버림 방향이라 정산 받는 쪽엔 손해가 가지 않는다. 테이블·엑셀 공용.)
 */
export const floorToTenth = (n: number): number => Math.floor(n * 10) / 10;

/** 테이블 입력 한 건 (정산 예정/배송완료 공통) */
export interface RebateTableEntry {
  /** populate된 배송 데이터 */
  delivery: GiftDeliveryForRebatePending;
  /** 수량 (정산 예정: preview.quantity, 배송완료: delivery.quantity) */
  quantity: number;
  /** 정산 설정(상조 giftAccountConfig) 존재 여부 — 경고 배지 표시용 */
  hasSettlementInfo: boolean;
}

/** 테이블 한 행의 계산 결과 */
export interface RebateRowCalc {
  /** 행 선택용 키(미정산 집행 선택 시 사용). 배송 _id. 상세 모달 등 선택 불필요 시 미설정 */
  rowKey?: string;
  /** 상품명 */
  itemName: string;
  /** 구매자(주문자)명 */
  receiverName: string;
  /** 고인명 (giftOrder.bugo.fevent.deceasedInfo.name) */
  deceasedName?: string;
  /** 발인일 (giftOrder.bugo.fevent.deceasedInfo.coffinOut.date) */
  coffinOutDate?: Date;
  /** 지도사 (giftOrder.bugo.fevent.user.info.name) */
  workerName?: string;
  /** 지도사 전화번호 (giftOrder.bugo.fevent.user.info.phoneNumber) */
  workerPhoneNumber?: string;
  /** 배송요청일 */
  submittedAt?: Date;
  /** 배송완료일 */
  deliveredAt?: Date;
  /** 판매가 (giftItemEmbed.priceRetail) */
  priceRetail: number | null;
  /** 상품원가 (giftItemEmbed.priceWhole) */
  priceWhole: number | null;
  /** 수수료 (FeeRate 방식만 계산, Rebate 방식이면 null) */
  fee: number | null;
  /** 건당 수익금 = 판매가 − 상품원가 − 수수료 */
  unitAmount: number | null;
  /** 수량 */
  quantity: number;
  /** 수익금 합계 = 수익금 × 수량 */
  lineAmount: number | null;
  /** 정산 설정 존재 여부 */
  hasSettlementInfo: boolean;
}

/**
 * 배송 1건 + 수량을 받아 테이블 한 행에 필요한 모든 값을 계산한다.
 *
 * - 가격은 주문 스냅샷(giftItemEmbed)에서만 읽는다.
 * - 수수료는 상조 giftAccountConfig.feeRate(기본 5.3%) 기준, FeeRate 방식에서만 계산한다.
 * - 수익금 = 판매가 − 상품원가 − 수수료, 수익금 합계 = 수익금 × 수량.
 */
export const computeRebateRow = (entry: RebateTableEntry): RebateRowCalc => {
  const { delivery, quantity, hasSettlementInfo } = entry;
  const giftItem = delivery.giftItem;
  const cfg = giftItem?.bugoBrand?.giftAccountConfig;
  const method = cfg?.method ?? 'FeeRate';

  const orderDetail = delivery.giftOrder?.orderDetails?.find(
    (od) => String(od.giftItem) === String(giftItem?._id),
  );
  const embed = orderDetail?.giftItemEmbed;
  const priceRetail = embed?.priceRetail ?? null;
  const priceWhole = embed?.priceWhole ?? null;

  const feeRate = cfg?.feeRate ?? DEFAULT_REBATE_FEE_RATE;
  const fee =
    method === 'FeeRate' && priceRetail != null ? priceRetail * (feeRate / 100) : null;

  const unitAmount =
    priceRetail != null && priceWhole != null && fee != null
      ? priceRetail - priceWhole - fee
      : null;

  const lineAmount = unitAmount != null ? unitAmount * quantity : null;

  // 고인명·발인일·지도사 (giftOrder.bugo.fevent populate 시에만 존재)
  const fevent = delivery.giftOrder?.bugo?.fevent;

  return {
    itemName: embed?.name ?? giftItem?.name ?? '-',
    receiverName: delivery.giftOrder?.receiverName || '-',
    deceasedName: fevent?.deceasedInfo?.name,
    coffinOutDate: fevent?.deceasedInfo?.coffinOut?.date,
    workerName: fevent?.user?.info?.name,
    workerPhoneNumber: fevent?.user?.info?.phoneNumber,
    submittedAt: delivery.recipient?.submittedAt,
    deliveredAt: delivery.shipping?.deliveredAt,
    priceRetail,
    priceWhole,
    fee,
    unitAmount,
    quantity,
    lineAmount,
    hasSettlementInfo,
  };
};

/** 테이블 합계 */
export interface RebateTotals {
  /** 건수 */
  totalCount: number;
  /** 총 수량 */
  totalQuantity: number;
  /** 총 수익금 = 총판매가 − 총상품원가 − 총수수료(내림) */
  totalAmount: number;
  /** 판매가 합 (판매가 × 수량의 합) */
  totalPriceRetail: number;
  /** 상품원가 합 (원가 × 수량의 합) */
  totalPriceWhole: number;
  /** 수수료 합 (수수료 × 수량의 합, 소수점 절삭) */
  totalFee: number;
  /** 수수료 합 (수수료 × 수량의 합, 절삭 전 — 소수점 포함, 테이블 푸터 표기용) */
  totalFeeRaw: number;
  /** 수익금 합 (수익금 × 수량의 합 = lineAmount 합, 소수점 포함, 테이블 푸터 표기용) */
  totalLineAmount: number;
  /** 정산 설정 미설정 건수 */
  missingSettlementCount: number;
}

/** 계산된 행 목록으로부터 합계를 집계한다. */
export const computeRebateTotals = (rows: RebateRowCalc[]): RebateTotals => {
  const totalPriceRetail = rows.reduce((sum, r) => sum + (r.priceRetail ?? 0) * r.quantity, 0);
  const totalPriceWhole = rows.reduce((sum, r) => sum + (r.priceWhole ?? 0) * r.quantity, 0);
  // 수수료 합(절삭 전, 소수점 포함) — 테이블 푸터는 이 값을 그대로 보여준다.
  const totalFeeRaw = rows.reduce((sum, r) => sum + (r.fee ?? 0) * r.quantity, 0);
  // 수수료 합은 소수점 절삭(floor) — 요약 카드/총 수익금 역산용
  const totalFee = Math.floor(totalFeeRaw);
  // 수익금 합(= lineAmount 합, 소수점 포함) — 테이블 푸터 수익금 컬럼 표기용
  const totalLineAmount = rows.reduce((sum, r) => sum + (r.lineAmount ?? 0), 0);
  // 총 수익금은 표시 항목으로 역산해 화면 합계가 정확히 맞도록 한다
  // (라인 합을 통째로 floor 하면 수수료 내림과 1원 어긋남)
  const totalAmount = totalPriceRetail - totalPriceWhole - totalFee;

  return {
    totalCount: rows.length,
    totalQuantity: rows.reduce((sum, r) => sum + r.quantity, 0),
    totalAmount,
    totalPriceRetail,
    totalPriceWhole,
    totalFee,
    totalFeeRaw,
    totalLineAmount,
    missingSettlementCount: rows.filter((r) => !r.hasSettlementInfo).length,
  };
};

/**
 * 정산 문서의 라인들로부터 "지급 예정액"을 정산 예정 테이블과 **동일한 공식**으로 계산한다.
 * = 총판매가 − 총상품원가 − 총수수료(소수점 절삭).
 *
 * computeRebateTotals 와 같은 식이며(정산 예정 합계와 일치 보장),
 * Draft(예정) 정산의 표시 금액을 맞추는 데 사용한다.
 * Paid(완료)는 이미 동결·지급된 `totalAmount` 를 그대로 쓴다(이 함수 사용 X).
 */
export const computeRebatePayableFromLines = (
  lines: GiftRebateSettlementLine[],
): number => {
  const totalPriceRetail = lines.reduce(
    (sum, l) => sum + (l.item.priceRetail ?? 0) * l.quantity,
    0,
  );
  const totalPriceWhole = lines.reduce(
    (sum, l) => sum + (l.item.priceWhole ?? 0) * l.quantity,
    0,
  );
  const totalFee = Math.floor(
    lines.reduce((sum, l) => {
      const pr = l.item.priceRetail;
      const pw = l.item.priceWhole;
      // 수수료(건당) = 판매가 − 상품원가 − 건당 수익금
      const fee = pr != null && pw != null ? pr - pw - l.unitAmount : 0;
      return sum + fee * l.quantity;
    }, 0),
  );
  return totalPriceRetail - totalPriceWhole - totalFee;
};

/**
 * 테이블 행을 배송완료일 오름차순으로 정렬한다(가장 오래된 건이 맨 위 = 1번).
 * 완료일이 없는 건은 맨 뒤로 보낸다. 원본 배열은 변경하지 않는다(복사 후 정렬).
 */
export const sortRebateRowsByDeliveredAt = (
  rows: RebateRowCalc[],
): RebateRowCalc[] =>
  [...rows].sort((a, b) => {
    const ta = a.deliveredAt ? new Date(a.deliveredAt).getTime() : Infinity;
    const tb = b.deliveredAt ? new Date(b.deliveredAt).getTime() : Infinity;
    return ta - tb;
  });

// ── 정산 라인 → 테이블 행 매핑 (정산 예정 / 완료 상세 모달 공유) ──

/** 장례 이벤트(fevent) — 고인명/발인일/지도사 표시용 */
interface RebateFevent {
  deceasedInfo?: { name?: string; coffinOut?: { date?: Date } };
  /** 담당 지도사 */
  user?: { info?: { name?: string; phoneNumber?: string } };
}

/**
 * /gift-rebate-settlement 응답의 line.giftDelivery 실제 populate 형태.
 * fevent 는 백엔드 버전에 따라 giftOrder.fevent 또는 giftOrder.bugo.fevent 로 올 수 있어
 * 양쪽을 모두 옵셔널로 둔다.
 */
interface RebateLineDelivery {
  recipient?: { submittedAt?: Date };
  shipping?: { deliveredAt?: Date };
  giftOrder?: {
    receiverName?: string;
    user?: { info?: { name?: string } };
    fevent?: RebateFevent;
    bugo?: { fevent?: RebateFevent };
  };
}

/**
 * 정산 문서의 라인 1건 → 테이블 행(RebateRowCalc).
 * - 상품명/금액: 집행 시 동결된 라인 스냅샷(line.item, line.unitAmount, line.lineAmount)
 * - 배송 메타(구매자/배송일/고인명/발인일/지도사): populate된 line.giftDelivery
 *
 * 정산 예정 테이블과 완료 내역 상세 모달이 동일하게 사용한다.
 */
export const settlementLineToRebateRow = (
  line: GiftRebateSettlementLine,
): RebateRowCalc => {
  const gd =
    typeof line.giftDelivery === 'object' && line.giftDelivery
      ? (line.giftDelivery as unknown as RebateLineDelivery)
      : undefined;
  // fevent: 백엔드 버전에 따라 giftOrder.fevent 또는 giftOrder.bugo.fevent
  const fevent = gd?.giftOrder?.fevent ?? gd?.giftOrder?.bugo?.fevent;

  const priceRetail = line.item.priceRetail ?? null;
  const priceWhole = line.item.priceWhole ?? null;
  // 수수료 = 판매가 − 상품원가 − 건당 수익금 (셋 다 있을 때만 역산)
  const fee =
    priceRetail != null && priceWhole != null
      ? priceRetail - priceWhole - line.unitAmount
      : null;

  return {
    itemName: line.item.name ?? '-',
    receiverName: gd?.giftOrder?.user?.info?.name || gd?.giftOrder?.receiverName || '-',
    deceasedName: fevent?.deceasedInfo?.name,
    coffinOutDate: fevent?.deceasedInfo?.coffinOut?.date,
    workerName: fevent?.user?.info?.name,
    workerPhoneNumber: fevent?.user?.info?.phoneNumber,
    submittedAt: gd?.recipient?.submittedAt,
    deliveredAt: gd?.shipping?.deliveredAt,
    priceRetail,
    priceWhole,
    fee,
    unitAmount: line.unitAmount,
    quantity: line.quantity,
    lineAmount: line.lineAmount,
    hasSettlementInfo: true,
  };
};

// ── 미정산(예정) 매퍼 / 정산단가 분해 ──

// 상조 정산단가 분해(판매가 − 도매가 − 수수료).
// 백엔드 preview(p.unitAmount)와 동일한 소스로 계산해 "판매가 − 도매가 − 수수료 = 정산단가"가
// 항상 맞아떨어지게 한다(판매가: 주문 스냅샷 우선, 도매가: giftItem.priceWhole — settle 동결 기준).
export interface RebateBreakdown {
  priceRetail: number | null;
  priceWhole: number | null;
  /** FeeRate 방식 건당 수수료(판매가 × feeRate%). Rebate 방식이면 null */
  fee: number | null;
  feeRate: number;
  isRebateMethod: boolean;
}

export const getRebateBreakdown = (d: GiftDeliveryForAdmin): RebateBreakdown => {
  const gi = typeof d.giftItem === 'object' ? (d.giftItem as any) : undefined;
  const cfg = gi?.bugoBrand?.giftAccountConfig;
  const feeRate = cfg?.feeRate ?? DEFAULT_REBATE_FEE_RATE;
  const isRebateMethod = cfg?.method === 'Rebate';
  const order = typeof d.giftOrder === 'object' ? (d.giftOrder as any) : undefined;
  const detail = order?.orderDetails?.find(
    (od: any) => String(od.giftItem) === String(gi?._id),
  );
  const priceRetail = detail?.giftItemEmbed?.priceRetail ?? gi?.priceRetail ?? null;
  const priceWhole = gi?.priceWhole ?? gi?.giftItemInfo?.priceWhole ?? null;
  const fee =
    !isRebateMethod && priceRetail != null ? (priceRetail * feeRate) / 100 : null;
  return { priceRetail, priceWhole, fee, feeRate, isRebateMethod };
};

// 미정산(예정) 배송 1건 → 상조정산 공용 테이블(RebateSettlementTable)의 행.
// 상세 페이지와 동일한 컬럼/계산을 쓰되, 도매가는 정산시점 라이브 값(getRebateBreakdown,
// = 백엔드 preview 기준)을 그대로 사용해 표시 금액이 preview와 어긋나지 않게 한다.
export const pendingEntryToRebateRow = (entry: SettlementPendingEntry): RebateRowCalc => {
  const d = entry.delivery;
  const p = entry.preview;
  const b = getRebateBreakdown(d);
  const order = typeof d.giftOrder === 'object' ? (d.giftOrder as any) : undefined;
  // fevent는 백엔드 버전에 따라 giftOrder.fevent 또는 giftOrder.bugo.fevent 로 온다
  const fevent = order?.fevent ?? order?.bugo?.fevent;
  return {
    rowKey: d._id,
    itemName: getItemName(d),
    receiverName: order?.receiverName || d.recipient?.name || '-',
    deceasedName: fevent?.deceasedInfo?.name,
    coffinOutDate: fevent?.deceasedInfo?.coffinOut?.date,
    workerName: fevent?.user?.info?.name,
    workerPhoneNumber: fevent?.user?.info?.phoneNumber,
    submittedAt: d.recipient?.submittedAt,
    deliveredAt: d.shipping?.deliveredAt,
    priceRetail: b.priceRetail,
    priceWhole: b.priceWhole,
    fee: b.isRebateMethod ? null : b.fee,
    unitAmount: p.unitAmount,
    quantity: p.quantity,
    lineAmount: p.lineAmount,
    hasSettlementInfo: p.hasSettlementInfo,
  };
};
